import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import client, { apiError } from "@/api/client";
import { PageHeader, Section, StatusPill } from "@/components/PageChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useUserNames } from "@/hooks/useUserNames";
import { toast } from "sonner";
import { ArrowLeft, DownloadSimple, XCircle, PlayCircle } from "@phosphor-icons/react";

const KINDS = {
  dispatched: {
    title: "Today's Dispatched — Report",
    subtitle: "Orders whose dispatch was recorded in the selected date range",
    dateField: "dispatch.at",
    stage: "dispatched",
    showValue: false,        // Order Value hidden; we show Bill Value instead
    showBillTotals: true,    // Billed Qty + Bill Value from Scanning & Count
    hasDateRange: true,
  },
  no_stock: {
    title: "No Stock — Report",
    subtitle: "Orders where Approx Box Quantity was entered as 0 in the selected date range",
    dateField: "picking.at",
    stage: "no_stock",
    showValue: false,
    hasDateRange: true,
  },
  hold: {
    title: "On Hold — Report",
    subtitle: "Active orders currently marked with a Hold By salesman",
    stage: "hold",
    showValue: false,
    hasDateRange: false,
  },
};

const money = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getDeep(obj, path) {
  return path.split(".").reduce((o, k) => (o == null ? o : o[k]), obj);
}

export default function PipelineReport() {
  const { kind = "dispatched" } = useParams();
  const nav = useNavigate();
  const meta = KINDS[kind] || KINDS.dispatched;
  const { nameFor } = useUserNames();
  const [orders, setOrders] = useState([]);
  const [start, setStart] = useState(todayIso());
  const [end, setEnd] = useState(todayIso());
  const [q, setQ] = useState("");
  const [removing, setRemoving] = useState(null);

  const reload = async () => { const { data } = await client.get("/orders"); setOrders(data); };
  useEffect(() => { reload(); }, []);

  // Reset dates when the kind changes so navigating between reports feels fresh
  useEffect(() => {
    setStart(todayIso()); setEnd(todayIso()); setQ("");
  }, [kind]);

  const rows = useMemo(() => {
    let list = orders;
    if (kind === "hold") {
      list = list.filter(o => !!(o.hold_by && String(o.hold_by).trim()) && o.stage !== "dispatched");
    } else {
      list = list.filter(o => o.stage === meta.stage);
    }
    if (meta.hasDateRange && (start || end)) {
      const s = start ? new Date(start + "T00:00:00") : null;
      const e = end ? new Date(end + "T23:59:59.999") : null;
      list = list.filter(o => {
        const raw = getDeep(o, meta.dateField);
        if (!raw) return false;
        const t = new Date(raw);
        if (isNaN(t.getTime())) return false;
        if (s && t < s) return false;
        if (e && t > e) return false;
        return true;
      });
    }
    const qs = q.trim().toLowerCase();
    if (qs) {
      list = list.filter(o =>
        (o.order_no || "").toLowerCase().includes(qs) ||
        (o.retailer_name || "").toLowerCase().includes(qs) ||
        (o.salesman || "").toLowerCase().includes(qs)
      );
    }
    return list;
  }, [orders, kind, meta.stage, meta.hasDateRange, meta.dateField, start, end, q]);

  const totals = useMemo(() => ({
    count: rows.length,
    qty:   rows.reduce((s, r) => s + Number(r.order_quantity || 0), 0),
    value: rows.reduce((s, r) => s + Number(r.order_value || 0), 0),
    pieces: rows.reduce((s, r) => s + Number(r.scanning?.total_pieces || 0), 0),
    boxes:  rows.reduce((s, r) => s + Number(r.scanning?.total_boxes || 0), 0),
    billed_qty:  rows.reduce((s, r) => s + Number(r.scanning?.billed_quantity || 0), 0),
    bill_value:  rows.reduce((s, r) => s + Number(r.scanning?.bill_value || 0), 0),
  }), [rows]);

  const exportCsv = () => {
    if (!rows.length) return;
    const header = ["Order No","Date","Company","Retailer","Salesman"];
    // Dispatched report: replace Qty/Value with Billed Qty/Bill Value (from Scanning)
    if (kind === "dispatched") {
      header.push("Billed Qty","Bill Value","Stage","Hold By","Remarks",
                  "Dispatched At","Bill No","Transporter","Boxes","Pieces");
    } else {
      header.push("Qty","Value","Stage","Hold By","Remarks");
      if (kind === "no_stock") {
        header.push("Picked At","Picked By","Picking Remarks");
      }
    }
    const lines = [header.join(",")];
    const cell = (v) => {
      const s = String(v ?? "").replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    for (const r of rows) {
      const base = [
        r.order_no, r.order_datetime ? new Date(r.order_datetime).toLocaleString() : "",
        r.company_name, r.retailer_name, r.salesman,
      ];
      let row;
      if (kind === "dispatched") {
        row = [
          ...base,
          r.scanning?.billed_quantity ?? "",
          r.scanning?.bill_value ?? "",
          r.stage,
          r.hold_by ? nameFor(r.hold_by) : "", r.remarks || "",
          r.dispatch?.at ? new Date(r.dispatch.at).toLocaleString() : "",
          r.dispatch?.bill_no || "", r.dispatch?.transporter_name || "",
          r.scanning?.total_boxes || "", r.scanning?.total_pieces || "",
        ];
      } else {
        row = [
          ...base,
          r.order_quantity, r.order_value ?? "", r.stage,
          r.hold_by ? nameFor(r.hold_by) : "", r.remarks || "",
        ];
        if (kind === "no_stock") {
          row.push(
            r.picking?.at ? new Date(r.picking.at).toLocaleString() : "",
            r.picking?.mobile ? nameFor(r.picking.mobile) : "",
            r.picking?.remarks || "",
          );
        }
      }
      lines.push(row.map(cell).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${kind}-report-${start || "all"}${meta.hasDateRange ? `_to_${end || "all"}` : ""}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const setPreset = (preset) => {
    const now = new Date();
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
    if (preset === "today") { setStart(iso(now)); setEnd(iso(now)); return; }
    if (preset === "yesterday") { const d = new Date(now); d.setDate(d.getDate()-1); setStart(iso(d)); setEnd(iso(d)); return; }
    if (preset === "week")  { const d = new Date(now); d.setDate(d.getDate()-6); setStart(iso(d)); setEnd(iso(now)); return; }
    if (preset === "month") { const d = new Date(now.getFullYear(), now.getMonth(), 1); setStart(iso(d)); setEnd(iso(now)); return; }
  };

  return (
    <div>
      <PageHeader
        title={meta.title}
        subtitle={meta.subtitle}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-sm" onClick={()=>nav("/")} data-testid="pr-back-btn">
              <ArrowLeft size={14} className="mr-1"/> Dashboard
            </Button>
            <Button className="bg-slate-900 hover:bg-slate-800 rounded-sm" onClick={exportCsv} disabled={!rows.length} data-testid="pr-export-btn">
              <DownloadSimple size={14} className="mr-1"/> CSV
            </Button>
          </div>
        }
      />
      <Section>
        <div className="bg-white border border-slate-200 rounded-sm p-4 mb-4 flex flex-wrap items-end gap-3">
          {meta.hasDateRange && (
            <>
              <div>
                <label className="label-cap">From</label>
                <Input data-testid="pr-date-from" type="date" value={start} onChange={(e)=>setStart(e.target.value)} className="rounded-sm"/>
              </div>
              <div>
                <label className="label-cap">To</label>
                <Input data-testid="pr-date-to" type="date" value={end} onChange={(e)=>setEnd(e.target.value)} className="rounded-sm"/>
              </div>
              <div className="flex flex-wrap gap-1 pb-0.5">
                {["today","yesterday","week","month"].map(p => (
                  <button key={p} data-testid={`pr-preset-${p}`} onClick={()=>setPreset(p)} className="text-xs px-2 py-1.5 rounded-sm border border-slate-300 bg-white hover:bg-slate-50 font-semibold text-slate-700 capitalize">
                    {p === "week" ? "Last 7d" : p === "month" ? "This month" : p}
                  </button>
                ))}
                {(start || end) && (
                  <button data-testid="pr-clear-dates" onClick={()=>{setStart("");setEnd("");}} className="text-xs px-2 py-1.5 rounded-sm border border-rose-300 text-rose-700 hover:bg-rose-50 font-semibold flex items-center gap-1">
                    <XCircle size={12}/> Clear
                  </button>
                )}
              </div>
            </>
          )}
          <div className="ml-auto">
            <label className="label-cap">Search</label>
            <Input data-testid="pr-search" placeholder="Order no / retailer / salesman" value={q} onChange={(e)=>setQ(e.target.value)} className="rounded-sm w-64"/>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="pr-totals">
          <Tile label="Orders" value={totals.count} accent="border-slate-400"/>
          {kind === "dispatched" ? (
            <>
              <Tile label="Billed Qty"      value={money(totals.billed_qty)} accent="border-blue-500"/>
              <Tile label="Bill Value (₹)"  value={money(totals.bill_value)} accent="border-emerald-500"/>
              <Tile label="Boxes"           value={money(totals.boxes)}      accent="border-violet-500"/>
              <Tile label="Pieces"          value={money(totals.pieces)}     accent="border-cyan-500"/>
            </>
          ) : (
            <>
              <Tile label="Order Qty" value={money(totals.qty)} accent="border-blue-500"/>
              {meta.showValue && <Tile label="Order Value (₹)" value={money(totals.value)} accent="border-emerald-500"/>}
            </>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left label-cap border-b border-slate-200 bg-slate-50">
                <th className="px-3 py-3">Order No</th>
                <th className="px-3 py-3">Date</th>
                <th className="px-3 py-3">Company</th>
                <th className="px-3 py-3">Retailer</th>
                <th className="px-3 py-3">Salesman</th>
                {kind === "dispatched" ? (
                  <>
                    <th className="px-3 py-3 text-right">Billed Qty</th>
                    <th className="px-3 py-3 text-right">Bill Value</th>
                  </>
                ) : (
                  <>
                    <th className="px-3 py-3 text-right">Qty</th>
                    {meta.showValue && <th className="px-3 py-3 text-right">Value</th>}
                  </>
                )}
                <th className="px-3 py-3">Stage</th>
                {kind === "hold"        && <><th className="px-3 py-3">Hold By</th><th className="px-3 py-3">Remarks</th><th className="px-3 py-3 w-40">Action</th></>}
                {kind === "dispatched"  && <><th className="px-3 py-3">Dispatched At</th><th className="px-3 py-3">Transporter</th><th className="px-3 py-3">Bill No</th></>}
                {kind === "no_stock"    && <><th className="px-3 py-3">Picked At</th><th className="px-3 py-3">Picked By</th><th className="px-3 py-3">Remarks</th></>}
              </tr>
            </thead>
            <tbody data-testid="pr-tbody">
              {rows.map(r => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono font-semibold">{r.order_no}</td>
                  <td className="px-3 py-2 text-xs">{r.order_datetime ? new Date(r.order_datetime).toLocaleDateString("en-IN") : "—"}</td>
                  <td className="px-3 py-2">{r.company_name || "—"}</td>
                  <td className="px-3 py-2">{r.retailer_name}</td>
                  <td className="px-3 py-2">{r.salesman}</td>
                  {kind === "dispatched" ? (
                    <>
                      <td className="px-3 py-2 text-right font-mono" data-testid={`pr-billed-qty-${r.id}`}>{r.scanning?.billed_quantity != null ? money(r.scanning.billed_quantity) : "—"}</td>
                      <td className="px-3 py-2 text-right font-mono" data-testid={`pr-bill-value-${r.id}`}>{r.scanning?.bill_value != null ? money(r.scanning.bill_value) : "—"}</td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-2 text-right font-mono">{r.order_quantity}</td>
                      {meta.showValue && <td className="px-3 py-2 text-right font-mono">{r.order_value ? money(r.order_value) : "—"}</td>}
                    </>
                  )}
                  <td className="px-3 py-2"><StatusPill status={r.stage}/></td>
                  {kind === "hold"       && <>
                    <td className="px-3 py-2 text-xs font-semibold">{r.hold_by ? nameFor(r.hold_by) : "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-600 max-w-xs" title={r.remarks || ""}>{r.remarks || "—"}</td>
                    <td className="px-3 py-2">
                      <Button
                        data-testid={`pr-remove-hold-${r.id}`}
                        disabled={removing === r.id}
                        onClick={async () => {
                          if (!window.confirm(`Remove hold on ${r.order_no}? It will re-enter the ${r.stage.replace("_", " ")} queue.`)) return;
                          setRemoving(r.id);
                          try {
                            const { data } = await client.post(`/orders/${r.id}/remove-hold`);
                            toast.success(`Hold removed — ${r.order_no} is back in flow (${data.resumed_stage})`);
                            await reload();
                          } catch (e) { toast.error(apiError(e)); }
                          finally { setRemoving(null); }
                        }}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-8 px-3 text-xs"
                      >
                        <PlayCircle size={12} weight="bold" className="mr-1"/>
                        {removing === r.id ? "Removing…" : "Remove Hold"}
                      </Button>
                    </td>
                  </>}
                  {kind === "dispatched" && <>
                    <td className="px-3 py-2 text-xs">{r.dispatch?.at ? new Date(r.dispatch.at).toLocaleString() : "—"}</td>
                    <td className="px-3 py-2 text-xs">{r.dispatch?.transporter_name || "—"}</td>
                    <td className="px-3 py-2 text-xs font-mono">{r.dispatch?.bill_no || "—"}</td>
                  </>}
                  {kind === "no_stock"   && <>
                    <td className="px-3 py-2 text-xs">{r.picking?.at ? new Date(r.picking.at).toLocaleString() : "—"}</td>
                    <td className="px-3 py-2 text-xs">{r.picking?.mobile ? nameFor(r.picking.mobile) : "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-600 max-w-xs">{r.picking?.remarks || "—"}</td>
                  </>}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={kind === "dispatched" ? 11 : (kind === "no_stock" ? 10 : 10)} className="px-4 py-10 text-center text-slate-500">
                  No orders in this report{meta.hasDateRange ? " for the selected date range" : ""}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 text-xs text-slate-500">
          <Link to="/" className="hover:underline">← Back to Dashboard</Link>
        </div>
      </Section>
    </div>
  );
}

function Tile({ label, value, accent = "border-slate-300" }) {
  return (
    <div className={`bg-white border-l-4 ${accent} rounded-sm p-3`}>
      <div className="label-cap truncate">{label}</div>
      <div className="text-xl font-black text-slate-900 mt-1">{value}</div>
    </div>
  );
}
